import { parseParams } from '~/utils/routes'

/**
 * Composable to normalize link values coming from CMS (Statamic) link fields.
 *
 * These fields deliver two shapes: entry links already carry the locale
 * prefix (the Statamic site URL includes it since JPR-625), while manually
 * entered URLs usually don't. A path that already starts with a valid
 * locale is kept as-is (it may deliberately point to another language
 * version), and external links must pass through untouched, since
 * useTargetPath would strip their host.
 *
 * @returns Function that localizes a CMS link for the current locale
 *
 * @example
 * const cmsLink = useCmsLink()
 * cmsLink('/fr/catalogue') // Returns '/fr/catalogue' (unchanged)
 * cmsLink('/fachkatalog') // Returns '/de/fachkatalog' (if de current locale)
 * cmsLink('https://example.com') // Returns 'https://example.com' (unchanged)
 */
export function useCmsLink() {
  const nuxt = useNuxtApp()
  const i18n = nuxt.$i18n
  const targetPath = useTargetPath()

  return (link: string): string => {
    // Absolute (https:, mailto:, tel:, ...), protocol-relative or
    // anchor-only links are not ours to localize
    if (/^(?:[a-z][a-z0-9+.-]*:|\/\/|#)/i.test(link)) {
      return link
    }
    if (parseParams(i18n.localeCodes.value, link).locale) {
      return link
    }
    return targetPath(link)
  }
}
